import { createContext, useContext } from "react";

export type Locale = "en" | "ar";

export const STRINGS = {
  en: {
    appName: "Retayf",
    tagline: "the work week, summarized",
    askAnything: "Ask anything, or press",
    teammates: "Teammates",
    addNote: "+ Add note",
    streak: (n: number) => `${n}-week streak`,
    weekOf: (n: number) => `Week ${n}`,
    myWeek: "Weekly Receipt",
    draftReady: "Draft of your weekly Receipt, ready to approve",
    draftSubcopy: (n: number) =>
      `We pulled ${n} items from your tools and notes. Take a look and approve when you're happy.`,
    approve: "Approve",
    edit: "Edit",
    headline: "Headline",
    highlights: "Highlights",
    shipped: "Shipped",
    blockers: "Blockers",
    thanks: "Thanks",
    save: "Save",
    cancel: "Cancel",
    voiceCapture: "Voice capture",
    listening: "Listening…",
    tapToSpeak: "Tap to speak",
    looksRight: "Looks right, log it",
    spokeForN: (n: number) => `You spoke for ${n} seconds. Receipt did the rest.`,
    settings: "Settings",
    workWeek: "Work week",
    weekStart: "Week starts on",
    workingDays: "Working days",
    locale: "Language",
    timezone: "Timezone",
    hijri: "Show Hijri dates",
    ramadan: "Ramadan-aware capacity",
    integrations: "Integrations",
    connect: "Connect",
    disconnect: "Disconnect",
    noActivity: "No activity captured yet for this day.",
    seedDemo: "Load demo data",
    days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    daysFull: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    confirm: "Confirm",
    react: "React",
    sharePreview: "Share preview",
    profile: "My profile",
    karmaReceived: "Karma received",
    recentReceipts: "Recent receipts",
    backToCanvas: "Back to my week",
    longestStreak: "Longest streak",
    weeksApproved: "Weeks approved",
    noRecentReceipts: "You haven’t approved any receipts yet. Your first one will appear here.",
    demoBannerTitle: "Try Receipt with sample data",
    demoBannerBody: "We’ll spin up a fictional team and a week of activity so you can see the canvas come alive.",
    demoBannerCta: "Try the demo",
    demoLoading: "Setting up your demo…",
    demoActiveLabel: "Clear demo data",
    demoActiveHelp: "Removes the fictional team, signals, and receipts.",
    demoCleared: "Demo data cleared",
    demoLoaded: "Demo loaded. Your week is populated",
    inviteTeammates: "Invite your team. See everyone’s day at a glance",
    invite: "Invite",
    inviteLinkCopied: "Invite link copied",
    inviteCreate: "Create invite link",
    inviteExpires: (days: number) => `Expires in ${days} days`,
    wrapUpToday: "Wrap up today",
    wrapUpHelp: "Draft today’s Receipt and approve in two taps.",
    comingSoon: "Coming soon (needs configuration)",
    retry: "Retry",
    errorLoadingProfile: "Couldn’t load your profile.",
    errorAskTeam: "Sorry, something went wrong answering that question.",
    profileEmptyKarma: "No karma yet. Once teammates react to your receipts you’ll see counts here.",
    editorStaleTitle: "Another tab updated this Receipt",
    editorStaleBody: "Reload to see the latest version. Your unsaved typing in this tab won’t apply.",
    installApp: "Install app",
    signOutEverywhere: "Sign out everywhere",
    signOutEverywhereHelp:
      "Invalidates every active session for your account on every device.",
    deleteAccount: "Delete account",
    deleteAccountHelp:
      "Permanently removes your account and all associated data: receipts, signals, voice memos, integrations, karma, streaks, and team memberships. This cannot be undone.",
    privacyPolicy: "Privacy policy",
    privacyTitle: "Privacy policy",
    privacyLastUpdated: "Last updated: September 7, 2026",
    privacyBackToApp: "Back to app",
    signInWithGoogle: "Sign in with Google",
    appearance: "Appearance",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    voiceLanguage: "Voice language",
    voiceLanguageHelp:
      "Auto-detect handles most cases. Bilingual runs two passes (Arabic + English) and merges. Best for code-switched recordings.",
    voiceLangAuto: "Auto-detect",
    voiceLangBilingual: "Bilingual (Arabic + English)",
    voiceLangEnglish: "English only",
    voiceLangArabic: "Arabic only",
  },
  ar: {
    appName: "ريتايف",
    tagline: "أسبوع العمل، مُلخّص",
    askAnything: "اسأل أي شيء، أو اضغط",
    teammates: "الفريق",
    addNote: "+ إضافة ملاحظة",
    streak: (n: number) => `${n} أسبوع متواصل`,
    weekOf: (n: number) => `الأسبوع ${n}`,
    myWeek: "الملخّص الأسبوعي",
    draftReady: "مسودة الـ Receipt الأسبوعية جاهزة للاعتماد",
    draftSubcopy: (n: number) =>
      `سحبنا ${n} عنصراً من أدواتك وملاحظاتك. اطّلع واعتمد عندما تكون راضياً.`,
    approve: "اعتماد",
    edit: "تعديل",
    headline: "العنوان",
    highlights: "أبرز ما تم",
    shipped: "تم تسليمه",
    blockers: "العوائق",
    thanks: "شكر",
    save: "حفظ",
    cancel: "إلغاء",
    voiceCapture: "التقاط صوتي",
    listening: "أستمع…",
    tapToSpeak: "اضغط للتحدّث",
    looksRight: "صحيح، سجّله",
    spokeForN: (n: number) => `تحدّثت لمدة ${n} ثانية. أكملت Receipt الباقي.`,
    settings: "الإعدادات",
    workWeek: "أسبوع العمل",
    weekStart: "يبدأ الأسبوع يوم",
    workingDays: "أيام العمل",
    locale: "اللغة",
    timezone: "المنطقة الزمنية",
    hijri: "إظهار التاريخ الهجري",
    ramadan: "تعديل القدرة في رمضان",
    integrations: "التكاملات",
    connect: "ربط",
    disconnect: "فصل",
    noActivity: "لم نلتقط أي نشاط بعد لهذا اليوم.",
    seedDemo: "تحميل بيانات تجريبية",
    days: ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"],
    daysFull: ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"],
    confirm: "تأكيد",
    react: "تفاعل",
    sharePreview: "معاينة المشاركة",
    profile: "ملفيّ",
    karmaReceived: "التفاعلات المستلمة",
    recentReceipts: "آخر الملخّصات",
    backToCanvas: "العودة إلى أسبوعي",
    longestStreak: "أطول تتابع",
    weeksApproved: "أسابيع معتمدة",
    noRecentReceipts: "لم تعتمد أي ملخّص بعد. أول واحد سيظهر هنا.",
    demoBannerTitle: "جرّب ريسيت ببيانات تجريبية",
    demoBannerBody: "سننشئ فريقاً وهمياً وأسبوعاً من النشاط لتشاهد المساحة تنبض بالحياة.",
    demoBannerCta: "جرّب العرض التجريبي",
    demoLoading: "نُهيّئ العرض التجريبي…",
    demoActiveLabel: "مسح البيانات التجريبية",
    demoActiveHelp: "يُزال الفريق الوهمي والإشارات والملخّصات.",
    demoCleared: "تم مسح البيانات التجريبية",
    demoLoaded: "تم تحميل العرض. أسبوعك جاهز",
    inviteTeammates: "ادعُ فريقك. شاهد يوم الجميع في نظرة",
    invite: "دعوة",
    inviteLinkCopied: "تم نسخ رابط الدعوة",
    inviteCreate: "إنشاء رابط دعوة",
    inviteExpires: (days: number) => `ينتهي خلال ${days} يوماً`,
    wrapUpToday: "أنهِ اليوم",
    wrapUpHelp: "حضّر ملخّص اليوم واعتمده بضغطتين.",
    comingSoon: "قريباً (يحتاج إعدادات)",
    retry: "إعادة المحاولة",
    errorLoadingProfile: "تعذّر تحميل ملفك.",
    errorAskTeam: "عذراً، حدث خطأ أثناء الإجابة عن السؤال.",
    profileEmptyKarma: "لا تفاعلات بعد. ستظهر هنا حين يتفاعل زملاؤك.",
    editorStaleTitle: "تم تحديث هذا الملخّص في علامة تبويب أخرى",
    editorStaleBody: "أعد التحميل لمشاهدة النسخة الأحدث. الكتابة غير المحفوظة هنا لن تُطبَّق.",
    installApp: "تثبيت التطبيق",
    signOutEverywhere: "تسجيل الخروج من كل الأجهزة",
    signOutEverywhereHelp:
      "يُلغي كل الجلسات النشطة لحسابك على كل الأجهزة.",
    deleteAccount: "حذف الحساب",
    deleteAccountHelp:
      "يحذف حسابك وجميع بياناتك بشكل دائم: الإيصالات، الإشارات، الملاحظات الصوتية، التكاملات، والعضويات. لا يمكن التراجع عن هذا الإجراء.",
    privacyPolicy: "سياسة الخصوصية",
    privacyTitle: "سياسة الخصوصية",
    privacyLastUpdated: "آخر تحديث: 7 سبتمبر 2026",
    privacyBackToApp: "العودة إلى التطبيق",
    signInWithGoogle: "تسجيل الدخول عبر Google",
    appearance: "المظهر",
    themeLight: "فاتح",
    themeDark: "داكن",
    themeSystem: "النظام",
    voiceLanguage: "لغة التسجيل الصوتي",
    voiceLanguageHelp:
      "الكشف التلقائي يكفي في معظم الحالات. الوضع ثنائي اللغة يُشغّل تحويلين متوازيين (عربي + إنجليزي) ويدمج النتائج. الأفضل للتسجيلات المختلطة.",
    voiceLangAuto: "كشف تلقائي",
    voiceLangBilingual: "ثنائي اللغة (عربي + إنجليزي)",
    voiceLangEnglish: "إنجليزي فقط",
    voiceLangArabic: "عربي فقط",
  },
};

export type Strings = typeof STRINGS["en"];

export const I18nContext = createContext<{
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Strings;
}>({
  locale: "en",
  setLocale: () => {},
  t: STRINGS.en,
});

export function useI18n() {
  return useContext(I18nContext);
}

export function isRtl(locale: Locale) {
  return locale === "ar";
}
